import { useId } from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { diamondGlows, DiamondGrid } from './primitives';
import { PartnerLogo } from './partner-logo';
import { Belt, Wheel } from './belt';
import { buildPortalHref } from './portal-href';

/** Which merchants the filter keeps out, and why: docs/notes/portals/www-live-figures.md */
export type PartnerMerchant = { id: string; name?: string; portalId?: string; logo?: { location: string } };

function LogoCard({ merchant }: { merchant: PartnerMerchant }) {
	// `pointer-events-none` stops a native image drag. `xl` must stay in sync with `./belt`.
	return (
		<div className="pointer-events-none flex h-24 w-56 items-center justify-center rounded-2xl bg-white px-5 shadow-[0_8px_24px_0_rgba(77,33,0,0.06)] select-none">
			{merchant.logo ? (
				// A lazy logo only loads once the belt has carried it on screen, which flickers the edges.
				<PartnerLogo src={merchant.logo.location} alt={merchant.name ?? merchant.id} size="xl" eager />
			) : (
				<span className="text-ink truncate text-sm font-semibold">{merchant.name}</span>
			)}
		</div>
	);
}

function LogoLink({
	merchant,
	baseUrl,
	className,
}: {
	merchant: PartnerMerchant;
	baseUrl: string;
	className?: string;
}) {
	if (!merchant.portalId) {
		return (
			<div className={className}>
				<LogoCard merchant={merchant} />
			</div>
		);
	}
	return (
		<a
			href={buildPortalHref(baseUrl, merchant.portalId)}
			// Another site: following one logo should not cost the reader the section.
			target="_blank"
			rel="noopener noreferrer"
			aria-label={merchant.name ?? merchant.id}
			title={merchant.name}
			className={cn('block', className)}
			draggable={false}
		>
			<LogoCard merchant={merchant} />
		</a>
	);
}

/** px per second, matching the desktop wheel. */
const MOBILE_SPEED = 32;

/** The per-item right margin is what the loop needs (see `Belt`). */
function LogoRowMobile({
	merchants,
	baseUrl,
	reverse,
}: {
	merchants: PartnerMerchant[];
	baseUrl: string;
	reverse?: boolean;
}) {
	// An empty row measures what the full one will, so the section never jumps while merchants stream in.
	if (merchants.length === 0) {
		return (
			<div aria-hidden className="pt-1 pb-5">
				<div className="h-24" />
			</div>
		);
	}
	return (
		// `clip` pairs with a visible cross axis; the padding is the shadow's reach, as `mask-image` clips to the border box.
		<div className="overflow-x-clip [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)] pt-1 pb-5">
			<Belt speed={MOBILE_SPEED} reverse={reverse}>
				{merchants.map((merchant, i) => (
					// oxlint-disable-next-line react/no-array-index-key
					<LogoLink key={i} merchant={merchant} baseUrl={baseUrl} className="mr-3 shrink-0" />
				))}
			</Belt>
		</div>
	);
}

function Center() {
	const { t } = useLingui();
	return (
		<div className="flex flex-col items-center gap-6 text-center">
			<h2 className="max-w-md text-3xl font-medium tracking-tight md:text-4xl">
				{t`Companies already making an impact with Worldcoo`}
			</h2>
			<Link
				to="/www/contact/$audience"
				params={{ audience: 'company' }}
				className="bg-brand hover:bg-brand-strong pointer-events-auto flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-black transition-colors"
			>
				{t`Contact us`}
				<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
					<ArrowUpRight className="size-4" />
				</span>
			</Link>
		</div>
	);
}

/**
 * SVG, since CSS snaps boxes to device pixels: at a 1.25 ratio a `w-px` line sat 0.5px off the ring's
 * centre. `useId`, as a literal id collides while this section and its fallback coexist.
 */
function Connector() {
	const fade = useId();
	return (
		<div className="text-brand relative mt-4 flex flex-col items-center xl:-mt-24" aria-hidden>
			<svg className="h-28 w-3 md:h-36" viewBox="0 0 12 144" preserveAspectRatio="none">
				<defs>
					<linearGradient id={fade} x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="currentColor" stopOpacity="0" />
						<stop offset="100%" stopColor="currentColor" stopOpacity="1" />
					</linearGradient>
				</defs>
				<rect x="5.5" y="0" width="1" height="144" fill={`url(#${fade})`} />
			</svg>
			<svg width="12" height="22" viewBox="0 0 12 22">
				<circle cx="6" cy="6" r="5" fill="white" stroke="currentColor" strokeWidth="2" />
				<rect x="5.5" y="12" width="1" height="10" fill="currentColor" />
			</svg>
		</div>
	);
}

export function Partners({
	connector = true,
	...props
}: {
	/** Omitted, the section is its own `Suspense` fallback at its final height; a hand-written one would drift. */
	merchants?: PartnerMerchant[];
	/** Part of the homepage composition: pages placing this section elsewhere turn it off. */
	connector?: boolean;
}) {
	const merchants = props.merchants ?? [];
	const { baseUrl } = useRouteContext({ from: '__root__' });
	const logos = merchants.filter((m) => m.logo);

	return (
		<section className="relative overflow-hidden bg-white">
			{/* `opacity` 1, as the glow's own 0.4 alpha is the intensity. */}
			<DiamondGrid opacity={1} masks={[diamondGlows({ x: '50%', y: 'calc(50% - 36px)' })]} />

			<div className="relative mx-auto max-w-[1440px] px-6 py-20 md:py-28 xl:px-10">
				<div className="relative hidden xl:block">
					<Wheel items={logos} renderItem={(merchant) => <LogoLink merchant={merchant} baseUrl={baseUrl} />} />
					<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
						<Center />
					</div>
				</div>

				<div className="xl:hidden">
					<Center />
					<div className="relative mt-8 flex flex-col gap-3">
						<LogoRowMobile merchants={logos.filter((_, i) => i % 2 === 0)} baseUrl={baseUrl} />
						<LogoRowMobile merchants={logos.filter((_, i) => i % 2 === 1)} baseUrl={baseUrl} reverse />
					</div>
				</div>
			</div>

			{connector && <Connector />}
		</section>
	);
}
